import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import { Link } from 'react-admin';
import type { AnalyzePrintingsRow } from './types';
import { FailureCausePicker } from './FailureCausePicker';

interface Props {
  rows: AnalyzePrintingsRow[];
  onPrintingChanged?: (id: number, failureCause: { id: number; name: string } | null) => void;
}

type SortKey =
  | 'id'
  | 'machine'
  | 'system'
  | 'user'
  | 'orderId'
  | 'startTime'
  | 'duration'
  | 'progress'
  | 'failureCause';

const durationMs = (r: AnalyzePrintingsRow): number => {
  if (!r.endTime) return 0;
  return Date.parse(r.endTime) - Date.parse(r.startTime);
};

const progressPct = (r: AnalyzePrintingsRow): number =>
  r.totalLines > 0 ? (100 * r.currentLine) / r.totalLines : 0;

const formatDuration = (ms: number): string => {
  if (ms <= 0) return '—';
  const s = Math.round(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${h}h ${m}m`;
};

const csvEscape = (s: string): string => (/[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s);

const toCsv = (rows: AnalyzePrintingsRow[]): string => {
  const header = [
    'Printing ID',
    'Machine',
    'System',
    'System User',
    'Order ID',
    'Start Time',
    'Duration',
    'Print %',
    'Failure Cause',
  ].join(',');
  const body = rows.map((r) =>
    [
      String(r.id),
      csvEscape(r.machine?.serial ?? ''),
      csvEscape(r.systemUser?.system?.releaseName ?? ''),
      csvEscape(r.systemUser?.username ?? ''),
      r.orderId ?? '',
      r.startTime,
      formatDuration(durationMs(r)),
      progressPct(r).toFixed(2),
      csvEscape(r.failureCause?.name ?? ''),
    ].join(',')
  );
  return [header, ...body].join('\n');
};

const downloadCsv = (content: string, filename: string) => {
  const blob = new Blob([content], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

export const PrintingsTable = ({ rows, onPrintingChanged }: Props) => {
  const [sortKey, setSortKey] = useState<SortKey>('startTime');
  const [asc, setAsc] = useState(false);

  const sorted = useMemo(() => {
    const comp = (a: AnalyzePrintingsRow, b: AnalyzePrintingsRow): number => {
      switch (sortKey) {
        case 'id':
          return a.id - b.id;
        case 'machine':
          return (a.machine?.serial ?? '').localeCompare(b.machine?.serial ?? '');
        case 'system':
          return (a.systemUser?.system?.releaseName ?? '').localeCompare(
            b.systemUser?.system?.releaseName ?? ''
          );
        case 'user':
          return (a.systemUser?.username ?? '').localeCompare(b.systemUser?.username ?? '');
        case 'orderId':
          return (a.orderId ?? 0) - (b.orderId ?? 0);
        case 'startTime':
          return Date.parse(a.startTime) - Date.parse(b.startTime);
        case 'duration':
          return durationMs(a) - durationMs(b);
        case 'progress':
          return progressPct(a) - progressPct(b);
        case 'failureCause':
          return (a.failureCause?.name ?? '').localeCompare(b.failureCause?.name ?? '');
        default:
          return 0;
      }
    };
    const copy = [...rows];
    copy.sort(comp);
    return asc ? copy : copy.reverse();
  }, [rows, sortKey, asc]);

  const header = (key: SortKey, label: string) => (
    <TableCell>
      <TableSortLabel
        active={sortKey === key}
        direction={asc ? 'asc' : 'desc'}
        onClick={() => {
          if (sortKey === key) setAsc(!asc);
          else {
            setSortKey(key);
            setAsc(true);
          }
        }}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" my={1}>
        <Typography variant="h6">Printings</Typography>
        <Button
          variant="outlined"
          onClick={() => downloadCsv(toCsv(sorted), 'analyze-printings.csv')}
          disabled={sorted.length === 0}
        >
          Export CSV
        </Button>
      </Box>
      <Table size="small">
        <TableHead>
          <TableRow>
            {header('id', 'Printing ID')}
            {header('machine', 'Machine')}
            {header('system', 'System')}
            {header('user', 'System User')}
            {header('orderId', 'Order ID')}
            {header('startTime', 'Start Time')}
            {header('duration', 'Duration')}
            {header('progress', 'Print %')}
            {header('failureCause', 'Failure Cause')}
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.length === 0 && (
            <TableRow>
              <TableCell colSpan={9}>No records found</TableCell>
            </TableRow>
          )}
          {sorted.map((r) => (
            <TableRow key={r.id}>
              <TableCell>{r.id}</TableCell>
              <TableCell>
                {r.machine ? (
                  <Link to={`/machines/${r.machine.id}/show`}>{r.machine.serial}</Link>
                ) : (
                  '—'
                )}
              </TableCell>
              <TableCell>
                {r.systemUser?.system ? (
                  <Link to={`/systems/${r.systemUser.system.id}/show`}>
                    {r.systemUser.system.releaseName}
                  </Link>
                ) : (
                  '—'
                )}
              </TableCell>
              <TableCell>
                {r.systemUser ? (
                  <Link to={`/system-users/${r.systemUser.id}/show`}>{r.systemUser.username}</Link>
                ) : (
                  '—'
                )}
              </TableCell>
              <TableCell>{r.orderId ?? '—'}</TableCell>
              <TableCell>{new Date(r.startTime).toLocaleString()}</TableCell>
              <TableCell>{formatDuration(durationMs(r))}</TableCell>
              <TableCell>{progressPct(r).toFixed(2)}</TableCell>
              <TableCell>
                <FailureCausePicker
                  printing={r}
                  onUpdated={(next) => onPrintingChanged?.(r.id, next)}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};
